import { InspectorPanelMetadata } from "../FlightPanelMetadata"
import { SidePanelLayer } from "@nova/constants"
import { InspectorPanelAccordionIntegrations } from "../accordion/integrations/InspectorPanelAccordionIntegrations"
import { useIconDownloadButtonData } from "./useIconDownloadButtonData"
import { useAppSelector } from "integrations/redux/hooks"
import { selectIconById } from "features/entity-caching/icons/selectors"
import { InspectorPanelAccordion } from "../accordion/InspectorPanelAccordion"
import { useMemo, useState } from "react"
import { Icon } from "@nova/protobuf"
import * as Icons from "@nova/component-icons"
import { ImageGrid, RenderUploadedIconImage } from "./InspectorPanelIconUpload"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { downloadBlob } from "tools/browser/download"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFire, selectActivatedFireId } from "features/map/control/selectors"
import { getIconImageS3Key } from "panels/content/side-panel/upload-flow/constants"
import { createS3DownloadUrlInBrowser } from "plugins/shared/s3url"
import { throttle } from "nodash"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { InspectorPanelAccordionExport } from "../accordion/export/InspectorPanelAccordionExport"
import { useExportLayers } from "features/modals/ExportLayers/useExportLayers"
import { selectEntityById } from "panels/content/side-panel/slice"
import { DownloadType } from "features/modals/ExportLayers/types"

export const InspectorPanelIconData = ({ id }: { id: string }) => {
    const icon = useAppSelector(selectIconById(id))
    const downloadOptions = useIconDownloadButtonData()
    const { handleDownloadClick } = useExportLayers()
    const layer = useAppSelector(selectEntityById(id))
    const project = useAppSelector(selectActivatedFire)
    return (
        <div className="mb-2 flex flex-col gap-2 shrink min-h-0">
            <IconExportAccordion icon={icon} />
            <InspectorPanelAccordionExport
                exportOptions={{
                    KML: {
                        callback: () =>
                            handleDownloadClick(
                                (project?.name as string) || "",
                                [layer as SidePanelLayer],
                                new Set([DownloadType.iconKml])
                            ),
                    },
                    SHP: {
                        callback: () =>
                            handleDownloadClick(
                                (project?.name as string) || "",
                                [layer as SidePanelLayer],
                                new Set([DownloadType.iconShp])
                            ),
                    },
                }}
                canOnlyPickOne={true}
            />

            {/* Integrations */}
            <InspectorPanelAccordionIntegrations
                id={id}
                integrationOptions={downloadOptions.integrationOptions}
            />

            <InspectorPanelMetadata entity={icon as SidePanelLayer} completedUploadCount={1} />
        </div>
    )
}

function IconExportAccordion({ icon }: { icon: Icon }) {
    const [isOpen, setIsOpen] = useState(false)
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const projectId = useAppSelector(selectActivatedFireId)
    const isEditor = useIsEditor()
    const downloadIconImage = useMemo(
        () =>
            throttle(
                async (
                    orgId: string,
                    projectId: string,
                    iconId: string,
                    fileId: string,
                    filename: string
                ) => {
                    const key = getIconImageS3Key({
                        orgId,
                        projectId,
                        flightId: iconId,
                        fileId,
                    })
                    const url = await createS3DownloadUrlInBrowser(key, false)
                    const blob = await fetch(url, { credentials: "include" }).then((r) => r.blob())
                    await downloadBlob(filename, blob)
                },
                2000
            ),
        []
    )
    if (!icon.images.length) return null
    return (
        <ScrollableArea>
            <InspectorPanelAccordion
                title="Export"
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                shouldPadChildrenVertically={false}
                extraVerticalPaddingInRem={0.5}
            >
                <div className="flex flex-col gap-2 pt-3 pb-1">
                    {icon.images.map((image) => (
                        <div key={image.id} className="flex gap-2 items-center text-gray-100">
                            <div className="w-[50px] h-[50px] rounded bg-red-200 overflow-hidden">
                                <RenderUploadedIconImage
                                    entityId={icon.id}
                                    imageId={image.id}
                                    isThumbnail
                                />
                            </div>
                            <small className="grow shrink min-w-0 truncate">{image.fileName}</small>
                            {workspaceId && projectId && icon && (
                                <div
                                    className="cursor-pointer hover:text-white"
                                    onClick={async () => {
                                        downloadIconImage(
                                            workspaceId,
                                            projectId,
                                            icon.id,
                                            image.id,
                                            image.fileName
                                        )
                                    }}
                                >
                                    <Icons.SmallIcon Icon={Icons.Download} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </InspectorPanelAccordion>

            {!isEditor && !!icon.images.length && (
                <ScrollableArea shouldPreventScrollbarShifting className="mt-2">
                    <div className="px-4">
                        <ImageGrid
                            entityId={icon.id}
                            imageIds={icon.images.map((image) => image.id)}
                            RenderImage={RenderUploadedIconImage}
                            allowClick
                        />
                    </div>
                </ScrollableArea>
            )}
        </ScrollableArea>
    )
}
